import type {
    Field,
    ImageField,
    ComponentRendering,
    ComponentParams,
} from "@sitecore-jss/sitecore-jss-nextjs";
import { useSitecoreContext, Text as JssText } from "@sitecore-jss/sitecore-jss-nextjs";
import type { ChakraStyledOptions } from "@chakra-ui/react";
import { Box, List, Text, ListItem, ListIcon, Container } from "@chakra-ui/react";
import BundleIcon from "commons/ui/BundleIcon";
import useImageSizes, { imageSize } from "utils/hooks/useImageSizes";
import SCHeading from "commons/sc/SCHeading";
import SCText from "commons/sc/SCText";
import SCLinkButton from "commons/sc/SCLinkButton";
import SCImage from "commons/sc/SCImage";
import type { GenericHeaderProps } from "commons/ui/GenericHeader";
import GenericHeader from "commons/ui/GenericHeader";
import { Fragment } from "react";

interface Fields {
    Headline: Field<string>;
    Text: Field<string>;
    CTA: Field<{ text: string; href: string }>;
    Image: ImageField;
    ElementHeader: Field<string>;
    ElementBullets: Field<string>;
    ElementText: Field<string>;
    OverlayOpacity: Field<number>;
}

interface Fields extends GenericHeaderProps {
    fields: Fields;
}

interface ShowcaseProps {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: Fields;
    asHero?: boolean;
}

interface ShowcaseBulletListProps {
    fields: Fields;
}

const ShowCaseBulletList = (props: ShowcaseBulletListProps) => {
    const bullets = props.fields?.ElementBullets?.value.split(/\r?\n/);
    const {
        sitecoreContext: { pageEditing },
    } = useSitecoreContext();
    return (
        <Box
            display="flex"
            flexDirection={"column"}
            gap="sp16"
            bg="white"
            px={{ base: "sp24", lg: "sp60" }}
            pt={{ base: "sp24", lg: "sp36" }}
            pb={{ base: "sp24", lg: "sp48" }}
            borderRadius={"base"}
            gridColumn={{ base: "1fr", lg: "span 3" }}
        >
            <SCHeading
                as="h2"
                textField={props.fields?.ElementHeader}
                size="md"
                id={props.fields.Id}
            />
            <SCText textField={props.fields?.ElementText} size="bodySmall" />

            {pageEditing && <JssText field={props.fields?.ElementBullets} />}
            {!pageEditing && props.fields?.ElementBullets?.value !== "" && (
                <List
                    pt={{ base: "sp16", lg: "sp32" }}
                    display={"grid"}
                    gridTemplateColumns={{ base: "1fr", lg: "1fr 1fr 1fr" }}
                    gap="sp16"
                    flexDirection={{ base: "column", lg: "row" }}
                    placeItems={"flex-start"}
                >
                    {bullets?.map((bullet, index) => {
                        return (
                            <ListItem
                                key={`${bullet}-${index}`}
                                display={"grid"}
                                gap="sp12"
                                gridTemplateColumns={"auto 1fr"}
                                alignItems={"top"}
                            >
                                <ListIcon as={BundleIcon} size="md" name="Verified" />
                                <Text as="span" size="body">
                                    {bullet.split(" # ").map((line, idx, arr) => (
                                        <Fragment key={idx}>
                                            {line.trim()}
                                            {idx < arr.length - 1 && (
                                                <>
                                                    <br />
                                                    <br />
                                                </>
                                            )}
                                        </Fragment>
                                    ))}
                                </Text>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

const ShowcaseContent = (
    props: ShowcaseProps & { sx?: ChakraStyledOptions; blackBackground?: boolean }
) => {
    const asHero = props.asHero ?? false;
    const sizes = useImageSizes({ base: 1, sm: 1, md: 2 / 3 });

    return (
        <Box
            display={"grid"}
            px={{ base: "sp16", lg: "sp60" }}
            py="sp48"
            gridTemplateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }}
            alignItems={"end"}
            rowGap={{ base: "sp40", lg: "sp96" }}
            borderRadius={"base"}
            {...(!asHero && { bg: "brand.lighterGray" })}
            sx={props.sx}
        >
            <Box display={"grid"} gap="sp16">
                <SCHeading
                    textField={props.fields?.Headline}
                    as="h2"
                    size={"xl"}
                    {...(asHero && { color: "white", fontSize: ["lg", null, null, "xl"] })}
                />
                <SCText
                    textField={props.fields?.Text}
                    size="bodyXl"
                    {...(asHero && { color: "white", fontSize: ["xxs", null, null, "md"] })}
                />
                <SCLinkButton
                    mt="sp24"
                    width={"fit-content"}
                    size="sm"
                    rightIcon={<BundleIcon name="ArrowForward" size="md" />}
                    linkField={props.fields?.CTA}
                    {...(asHero && { variant: "secondary" })}
                >
                    {props.fields?.CTA.value.text}
                </SCLinkButton>
            </Box>

            {(!asHero || props.blackBackground) && (
                <SCImage
                    sizes={sizes}
                    imageField={props.fields?.Image}
                    {...imageSize(Number(props.fields?.Image?.value?.width), 4, 3)}
                    sx={{ objectFit: "contain", width: "100%", height: "100%" }}
                    {...(props.blackBackground && {
                        aspectRatio: "4/3",
                        sx: {
                            width: "66.6666%",
                            height: "auto",
                            objectFit: "cover",
                            position: "absolute",
                            top: 0,
                            left: "33.3333%",
                            zIndex: -1,
                        },
                    })}
                    {...(!props.blackBackground && { aspectRatio: "4/3", gridColumn: "span 2" })}
                />
            )}

            <ShowCaseBulletList {...props} />
        </Box>
    );
};

const Showcase = (props: ShowcaseProps) => {
    const sizes = useImageSizes({ base: 1, sm: 1, md: 1 });
    const asHero = props.asHero ?? false;
    const blackBackground = props.rendering?.params?.Styles?.includes("black_background") ?? false;
    const overlay = props.fields?.OverlayOpacity?.value ?? 0;

    if (!props.fields) return null;

    if (asHero) {
        return (
            <Box
                as="section"
                {...(blackBackground && { bg: "black" })}
                display="flex"
                flexDirection="column"
                position="relative"
                marginTop={[
                    "calc(var(--sizes-headerMobileTopHeight) * -1)",
                    "calc(var(--sizes-headerTopHeight) * -1)",
                ]}
                sx={{
                    py: [
                        "calc(var(--sizes-headerMobileTopHeight))",
                        null,
                        null,
                        "calc(var(--sizes-headerTopHeight))",
                    ],
                    ...(!blackBackground && {
                        _after: {
                            content: "''",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            backgroundColor: `rgba(0, 0, 0, ${overlay / 100})`,
                            width: "100%",
                            height: "100%",
                            zIndex: -1,
                        },
                    }),
                }}
            >
                <Container position="relative" zIndex={2} h={"full"}>
                    <ShowcaseContent
                        {...props}
                        sx={{ height: "100%" }}
                        blackBackground={blackBackground}
                    />
                </Container>

                {!blackBackground && (
                    <SCImage
                        sizes={sizes}
                        imageField={props.fields?.Image}
                        {...imageSize(Number(props.fields?.Image?.value?.width), 4, 3)}
                        sx={{
                            objectFit: "cover",
                            width: "100%",
                            height: "100%",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            zIndex: -1,
                        }}
                        gridColumn="span 2"
                        aspectRatio={"4/3"}
                    />
                )}
            </Box>
        );
    }

    return (
        <GenericHeader
            Id={props.rendering?.uid ?? props.rendering?.componentName}
            AnchorId={props.fields?.AnchorId}
            HeaderHeadline={props.fields?.HeaderHeadline}
            HeaderSubHeadline={props.fields?.HeaderSubHeadline}
            HeaderCTA={props.fields?.HeaderCTA}
            headlineSize={props.params?.HeaderHeadlineSize}
            spacingSize={props.params?.Padding}
        >
            <ShowcaseContent {...props} />
        </GenericHeader>
    );
};

export const Default = (props: ShowcaseProps) => {
    return <Showcase fields={props.fields} rendering={props.rendering} params={props.params} />;
};

export const AsHero = (props: ShowcaseProps) => {
    return (
        <Showcase asHero fields={props.fields} rendering={props.rendering} params={props.params} />
    );
};

// export default Showcase;
